import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import {
  ExternalLink,
  Building2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'
import { formatRelativeTime } from '@super/shared-types'
import { PageHeader } from '@/components/layout/PageHeader'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAppAuth } from '@/contexts/app-auth.context'
import { partnerClient } from '@/lib/api/partner.client'

interface ProvisionedCompany {
  id: string
  name: string
  slug: string
  status: string
  integrationStatus: string
  provisionedAt: string
  lastSyncStatus?: string | null
  lastSyncCompletedAt?: string | null
  lastSyncError?: string | null
  configuredAgents?: { name: string; templateKey: string | null }[]
}

const formatDate = (dateString: string) => {
  if (!dateString) return 'Never'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const getStatusColor = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
    case 'CONNECTED':
      return 'default'
    case 'PENDING':
      return 'secondary'
    case 'SUSPENDED':
    case 'DISCONNECTED':
      return 'outline'
    case 'DELETED':
      return 'destructive'
    default:
      return 'outline'
  }
}

function SyncStatusBadge({ company }: { company: ProvisionedCompany }) {
  if (company.integrationStatus === 'DISCONNECTED') {
    return (
      <Badge variant="destructive">
        <AlertCircle className="h-3 w-3 mr-1" />
        Disconnected
      </Badge>
    )
  }

  if (company.lastSyncStatus === 'SUCCESS' && company.lastSyncCompletedAt) {
    const completedAt = new Date(company.lastSyncCompletedAt)
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className="bg-success-muted text-success border-success-border cursor-default"
          >
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Synced {formatRelativeTime(completedAt)}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>{completedAt.toLocaleString()}</TooltipContent>
      </Tooltip>
    )
  }

  if (company.lastSyncStatus === 'FAILED') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className="bg-error-muted text-error border-error-border cursor-default"
          >
            <AlertCircle className="h-3 w-3 mr-1" />
            Sync failed
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          {company.lastSyncError || 'Unknown error'}
        </TooltipContent>
      </Tooltip>
    )
  }

  // Connected but no sync yet
  return (
    <Badge
      variant="outline"
      className="bg-success-muted text-success border-success-border"
    >
      <CheckCircle2 className="h-3 w-3 mr-1" />
      Connected
    </Badge>
  )
}

export function PartnerCompaniesPage() {
  const {
    isSuperAdmin,
    currentCompanyPartner,
    isCompanyLoading,
    isInitialized,
  } = useAppAuth()
  const [companies, setCompanies] = useState<ProvisionedCompany[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)

  useEffect(() => {
    // Wait for auth to initialize and company/partner loading to complete
    if (!isInitialized || isCompanyLoading) return

    // For super admins, only fetch if viewing a partner company
    if (isSuperAdmin && !currentCompanyPartner) {
      setLoading(false)
      setCompanies([])
      return
    }

    loadCompanies()
  }, [isSuperAdmin, currentCompanyPartner?.id, isCompanyLoading, isInitialized])

  const loadCompanies = async () => {
    try {
      setLoading(true)
      // Use admin endpoint if super admin viewing a partner company
      const data =
        isSuperAdmin && currentCompanyPartner
          ? await partnerClient.getProvisionedCompaniesAsAdmin(
              currentCompanyPartner.id,
            )
          : await partnerClient.getProvisionedCompanies()
      setCompanies(data.companies || [])
      setTotal(data.total || 0)
    } catch (error) {
      toast.error('Failed to load companies')
      console.error('Error loading companies:', error)
      setCompanies([])
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="h-full overflow-y-auto mt-16">
      <FloatingActionIslandProvider>
        <div className="space-y-6 p-6 pt-0">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mt-6">
            <PageHeader title="View all API-provisioned companies" />
            {total > 0 && (
              <Badge variant="secondary" className="text-sm w-fit">
                {total} {total === 1 ? 'Company' : 'Companies'}
              </Badge>
            )}
          </div>

          <Card>
            <CardContent className="p-0">
              {loading || companies === null ? (
                <div className="space-y-0">
                  <div className="border-b">
                    <div className="flex space-x-4 px-6 py-3.5">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </div>
                  <div className="divide-y">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex space-x-4 px-6 py-4">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : companies.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <Building2 className="h-8 w-8 text-gray-400 mb-4" />
                  <h3 className="mb-2 text-lg font-semibold">
                    No companies yet
                  </h3>
                  <p className="mb-6 max-w-sm text-sm text-muted-foreground">
                    Companies provisioned through the Partner API will appear
                    here
                  </p>
                  <Button variant="outline" asChild>
                    <a
                      href="https://docs.hiresuper.com/partner-api"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View API Documentation
                      <ExternalLink className="ml-2 h-3 w-3" />
                    </a>
                  </Button>
                </div>
              ) : (
                <TooltipProvider>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company name</TableHead>
                        <TableHead>Slug</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Sync Status</TableHead>
                        <TableHead>Agents</TableHead>
                        <TableHead>Provisioned</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {companies.map(company => (
                        <TableRow key={company.id}>
                          <TableCell className="font-medium">
                            {company.name}
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {company.slug}
                            </code>
                          </TableCell>
                          <TableCell>
                            <Badge variant={getStatusColor(company.status)}>
                              {company.status || 'UNKNOWN'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <SyncStatusBadge company={company} />
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {company.configuredAgents?.map(agent => (
                                <Tooltip key={agent.name}>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant="outline"
                                      className="cursor-default"
                                    >
                                      {agent.name}
                                    </Badge>
                                  </TooltipTrigger>
                                  {agent.templateKey && (
                                    <TooltipContent>
                                      {agent.templateKey}
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            {formatDate(company.provisionedAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TooltipProvider>
              )}
            </CardContent>
          </Card>

          <FloatingActionIsland />
        </div>
      </FloatingActionIslandProvider>
    </div>
  )
}
